import { type FC, memo, useEffect, useCallback } from 'react';
import InputSection from './input-section';
import plusIcon from '@assets/images/vault/plus.svg';
import styles from './index.module.scss';
import { type CoinStruct } from '@models/coins.vault.model';
import { Decimal } from 'parrot-sui-sdk';

import { Segmented } from 'antd';
import Slider from '@ui/slider';
import { type QuoteResponse } from '@7kprotocol/sdk-ts';

interface OwnProps {
  coinA: CoinStruct;
  coinB: CoinStruct;
  availableA: string;
  availableB: string;
  tabValue: number;
  amountA: string;
  amountB: string;
  onChangeAmountA(amountA: string): void;
  onChangeAmountB(amountB: string): void;
  onChangeExactIn(value: boolean): void;
  onChangePercent(value: number): void;
  percent: number;
  aggregatorRoute: QuoteResponse | undefined;
}

const WithdrawAmount: FC<OwnProps> = (props) => {
  const {
    coinA,
    coinB,
    tabValue,
    onChangeAmountA,
    onChangeAmountB,
    amountA,
    amountB,
    onChangeExactIn,
    availableA,
    availableB,
    onChangePercent,
    percent,
    aggregatorRoute,
  } = props;

  const vaultTabs = [
    {
      label: <div className={styles.tab_type}>25%</div>,
      value: 25,
    },
    {
      label: <div className={styles.tab_type}>50%</div>,
      value: 50,
    },
    {
      label: <div className={styles.tab_type}>75%</div>,
      value: 75,
    },
    {
      label: <div className={styles.tab_type}>Max</div>,
      value: 100,
    },
  ];

  useEffect(() => {
    onChangePercent(0);
  }, [tabValue, onChangePercent]);

  const handleAmountAChanged = useCallback(
    (value: string) => {
      onChangeExactIn(true);
      onChangeAmountA(value);
      const percent = new Decimal(value).div(availableA);
      onChangePercent(percent.lt(1) ? percent.mul(100).toNumber() : 100);
      onChangeAmountB(percent.mul(availableB).toFixed(0));
    },
    [availableA, availableB],
  );

  const handleAmountBChanged = useCallback(
    (value: string) => {
      onChangeExactIn(false);
      onChangeAmountB(value);
      const percent = new Decimal(value).div(availableB);
      onChangePercent(percent.lt(1) ? percent.mul(100).toNumber() : 100);
      onChangeAmountA(percent.mul(availableA).toFixed(0));
    },
    [availableA, availableB],
  );

  const handlePercent = useCallback(
    (percent: number) => {
      if (!aggregatorRoute && (tabValue === 3 || tabValue === 2)) {
        return;
      }
      onChangePercent(percent);
      onChangeAmountA(new Decimal(percent).div(100).mul(availableA).toFixed(0));
      onChangeAmountB(new Decimal(percent).div(100).mul(availableB).toFixed(0));
    },
    [availableA, availableB, aggregatorRoute],
  );

  return (
    <div className={`${styles.wrapper}`}>
      <div className={styles.main}>
        {tabValue !== 3 && (
          <InputSection
            coin={coinA}
            amount={amountA}
            onAmountChange={handleAmountAChanged}
            balance={availableA}
            disabled={!aggregatorRoute && tabValue === 2}
            loading={availableB !== '0' && !aggregatorRoute && tabValue === 2}
          />
        )}
        {tabValue === 1 && (
          <>
            <div className={styles.plus}>
              <img src={plusIcon} />
            </div>
            <div className={styles.space}></div>
          </>
        )}

        {tabValue !== 2 && (
          <InputSection
            coin={coinB}
            amount={amountB}
            onAmountChange={handleAmountBChanged}
            balance={availableB}
            disabled={!aggregatorRoute && tabValue === 3}
            loading={availableA !== '0' && !aggregatorRoute && tabValue === 3}
          />
        )}
      </div>
      <div className={styles.slider_title}>Amount</div>
      <div className={styles.slider_tab}>
        <div className={styles.slider_percent}>{Number(percent.toFixed(2))}%</div>
        <div className={styles.slider_select}>
          <Segmented value={percent} options={vaultTabs} onChange={handlePercent} />
        </div>
      </div>

      <div className={styles.slider}>
        <Slider min={0} max={100} value={percent} onChange={handlePercent} />
      </div>
    </div>
  );
};

export default memo(WithdrawAmount);
